import { Alert, Avatar, Chip, Spinner } from '@billda/design-system';
import {
  VisibilityIcon,
  VisibilityOffIcon,
  PercentIcon,
} from '@billda/design-system';
import { StepNavigation } from '../../molecules/StepNavigation';
import { GangFinancialSummary } from '../../molecules/GangFinancialSummary';
import { GangRoleBadge } from '../../atoms/GangRoleBadge';
import { GangLifecycleMode } from '../../../stores/gangLifecycleStore';
import { UseFormReturn } from 'react-hook-form';
import {
  GangCreationFormData,
  GangParticipant,
} from '../../../types/gangCreationTypes';

const getInitials = (name: string) => {
  if (!name) {
    return 'U';
  }

  return name
    .split(' ')
    .filter(Boolean)
    .map(part => part.charAt(0))
    .join('')
    .toUpperCase()
    .slice(0, 2);
};

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(amount);

const renderSplitChip = (
  percentage: number | null | undefined,
  isEqualSplit: boolean,
  equalPercentage: number
) => {
  // When equal split is enabled, show the calculated equal percentage
  if (isEqualSplit) {
    return (
      <Chip
        key="equal-split"
        icon={<PercentIcon aria-hidden="true" className="size-3" />}
        label={`${equalPercentage}% (Equal Split)`}
        size="sm"
        color="info"
        variant="outlined"
        className="font-semibold"
      />
    );
  }

  if (percentage === null || percentage === undefined) {
    return (
      <Chip
        key="no-percentage"
        label="No split percentage"
        size="sm"
        color="default"
        variant="outlined"
        className="italic"
      />
    );
  }

  // Round to 1 d.p. to avoid floating-point display issues (e.g., 33.300000000000004%)
  const roundedPercentage = Math.round(percentage * 10) / 10;

  return (
    <Chip
      key="percentage"
      icon={<PercentIcon aria-hidden="true" className="size-3" />}
      label={`${roundedPercentage}%`}
      size="sm"
      color="info"
      variant="outlined"
      className="font-semibold"
    />
  );
};

const renderFixedRateChip = (
  defaultRate: number | null | undefined,
  defaultUnitsValue: number | null | undefined
) => {
  if (defaultRate === null || defaultRate === undefined) {
    return (
      <Chip
        key="no-fixed-rate"
        label="No fixed rate"
        size="sm"
        color="default"
        variant="outlined"
        className="italic"
      />
    );
  }

  const rateLabel =
    defaultUnitsValue !== null && defaultUnitsValue !== undefined
      ? `${formatCurrency(defaultRate)} × ${defaultUnitsValue} units`
      : formatCurrency(defaultRate);

  return (
    <Chip
      key="fixedRate"
      label={rateLabel}
      size="sm"
      color="secondary"
      variant="outlined"
      className="font-semibold"
    />
  );
};

const renderVisibilityChip = (canSeeSplit: boolean) => (
  <Chip
    icon={
      canSeeSplit ? (
        <VisibilityIcon aria-hidden="true" className="size-3" />
      ) : (
        <VisibilityOffIcon aria-hidden="true" className="size-3" />
      )
    }
    label={canSeeSplit ? 'Can see splits' : 'Cannot see splits'}
    size="sm"
    color={canSeeSplit ? 'success' : 'warning'}
    variant={canSeeSplit ? 'filled' : 'outlined'}
    className="font-semibold"
  />
);

export interface GangReviewStepProps {
  form: UseFormReturn<GangCreationFormData>;
  mode: GangLifecycleMode;
  onBack: () => void;
  onSubmit: () => void;
  isSubmitting: boolean;
  hasChanges: boolean;
  isValid?: boolean;
}

export const GangReviewStep = ({
  form,
  mode,
  onBack,
  onSubmit,
  isSubmitting,
  hasChanges,
  isValid = true,
}: GangReviewStepProps) => {
  const gangName = form.watch('name');
  const leaderIds = form.watch('leaderIds') ?? [];
  const memberIds = form.watch('memberIds') ?? [];
  const participants = form.watch('participants') ?? {};
  const isEqualSplit = form.watch('isEqualSplit') ?? true;

  const leaderParticipants: GangParticipant[] = leaderIds
    .map(userId => participants[userId])
    .filter((participant): participant is GangParticipant =>
      Boolean(participant)
    );
  const memberParticipants: GangParticipant[] = memberIds
    .map(userId => participants[userId])
    .filter((participant): participant is GangParticipant =>
      Boolean(participant)
    );

  // Count percentage members and calculate equal split
  const percentageMemberCount = [
    ...leaderParticipants,
    ...memberParticipants,
  ].filter(p => p.paymentType === 'percentage').length;

  const equalPercentage =
    percentageMemberCount > 0
      ? Math.round((100 / percentageMemberCount) * 10) / 10
      : 0;

  const totalMembers = leaderParticipants.length + memberParticipants.length;
  const totalRegularMembers = memberParticipants.length;

  // When equal split is enabled, total is always 100%
  // Otherwise, sum up the stored percentages
  const totalPercentageSplit = isEqualSplit
    ? percentageMemberCount > 0
      ? 100
      : 0
    : [...leaderParticipants, ...memberParticipants]
        .filter(
          member =>
            member.defaultSplitPercentage !== null &&
            member.defaultSplitPercentage !== undefined
        )
        .reduce((sum, member) => sum + (member.defaultSplitPercentage || 0), 0);

  const hasAtLeastTwoMembers = totalMembers >= 2;
  const hasAtLeastOneLeader = leaderParticipants.length >= 1;
  const meetsMinimumRequirements = hasAtLeastTwoMembers && hasAtLeastOneLeader;

  const disableSubmit =
    isSubmitting ||
    !isValid ||
    !meetsMinimumRequirements ||
    (mode === 'edit' && !hasChanges && totalMembers > 0);

  return (
    <div className="py-4">
      <p className="type-body-lg mb-4">
        Review your configuration before{' '}
        {mode === 'create' ? 'creating the gang.' : 'saving changes.'}
      </p>

      <div className="mb-6">
        <p className="type-body-lg font-medium">Gang Name</p>
        <p
          className="type-body-md text-content-secondary"
          data-testid="gang-review-name-value"
        >
          {gangName}
        </p>
      </div>

      <div className="mb-6">
        <p className="type-body-lg font-medium">Team Summary</p>
        <p
          className="type-body-md text-content-secondary"
          data-testid="gang-review-team-summary"
        >
          {leaderParticipants.length} leader
          {leaderParticipants.length === 1 ? '' : 's'} &bull;{' '}
          {totalRegularMembers} member{totalRegularMembers === 1 ? '' : 's'}{' '}
          &bull; {totalMembers} total participant{totalMembers === 1 ? '' : 's'}
        </p>
      </div>

      <GangFinancialSummary
        leaders={leaderParticipants}
        members={memberParticipants}
        isEqualSplit={isEqualSplit}
      />

      {!isEqualSplit && totalPercentageSplit > 100 && (
        <Alert
          severity="error"
          className="mt-4"
          data-testid="gang-review-overallocation-alert"
        >
          Default splits exceed 100%. Adjust the percentages before continuing.
        </Alert>
      )}

      {!hasAtLeastTwoMembers && (
        <Alert
          severity="error"
          className="mt-4"
          data-testid="gang-review-minimum-members-alert"
        >
          A gang must have at least 2 members. Please add more members.
        </Alert>
      )}

      {!hasAtLeastOneLeader && (
        <Alert
          severity="error"
          className="mt-4"
          data-testid="gang-review-minimum-leaders-alert"
        >
          A gang must have at least 1 leader. Please add a leader.
        </Alert>
      )}

      {mode === 'edit' && !hasChanges && (
        <Alert
          severity="info"
          className="mt-4"
          data-testid="gang-review-no-changes-alert"
        >
          No changes detected yet. Update the gang details to enable saving.
        </Alert>
      )}

      <div className="mt-8">
        <p className="type-body-lg mb-4 font-medium">Leaders</p>

        <ul
          className="m-0 list-none p-0"
          data-testid="gang-review-leaders-list"
        >
          {leaderParticipants.map(leader => (
            <li
              key={leader.userId}
              className="flex items-start gap-4 px-0 py-2"
              data-testid={`gang-review-leader-${leader.userId}`}
            >
              <Avatar
                aria-label={leader.fullName}
                className="bg-surface-primary text-on-primary"
              >
                {getInitials(leader.fullName)}
              </Avatar>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="type-body-lg font-medium">
                    {leader.fullName}
                  </span>
                  <GangRoleBadge role="leader" />
                </div>
                <div className="flex flex-col gap-0.5">
                  <p className="type-body-md text-content-secondary">
                    {leader.email || 'Email unavailable'}
                  </p>
                  <div className="mt-0.5">{renderVisibilityChip(true)}</div>
                  <div className="mt-0.5 flex flex-wrap gap-2">
                    {leader.paymentType === 'percentage'
                      ? renderSplitChip(
                          leader.defaultSplitPercentage,
                          isEqualSplit,
                          equalPercentage
                        )
                      : renderFixedRateChip(
                          leader.defaultRate,
                          leader.defaultUnitsValue
                        )}
                  </div>
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>

      <div className="mt-8">
        <p className="type-body-lg mb-4 font-medium">Members</p>

        {memberParticipants.length === 0 ? (
          <p className="type-body-md text-content-secondary">
            No additional members selected.
          </p>
        ) : (
          <ul
            className="m-0 list-none p-0"
            data-testid="gang-review-members-list"
          >
            {memberParticipants.map(member => (
              <li
                key={member.userId}
                className="flex items-start gap-4 px-0 py-2"
                data-testid={`gang-review-member-${member.userId}`}
              >
                <Avatar aria-label={member.fullName}>
                  {getInitials(member.fullName)}
                </Avatar>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="type-body-lg font-medium">
                      {member.fullName}
                    </span>
                    <GangRoleBadge role="member" />
                  </div>
                  <div className="flex flex-col gap-0.5">
                    <p className="type-body-md text-content-secondary">
                      {member.email || 'Email unavailable'}
                    </p>
                    <div className="mt-0.5">
                      {renderVisibilityChip(Boolean(member.canSeeSplit))}
                    </div>
                    <div className="mt-0.5 flex flex-wrap gap-2">
                      {member.paymentType === 'percentage'
                        ? renderSplitChip(
                            member.defaultSplitPercentage,
                            isEqualSplit,
                            equalPercentage
                          )
                        : renderFixedRateChip(
                            member.defaultRate,
                            member.defaultUnitsValue
                          )}
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <StepNavigation
        onPrevious={onBack}
        onNext={onSubmit}
        nextLabel={mode === 'create' ? 'Create Gang' : 'Save Changes'}
        disableNext={disableSubmit}
        endAdornment={isSubmitting ? <Spinner size={24} /> : undefined}
      />
    </div>
  );
};
